import { useContext, useState } from "react";

import { Button, Link, Typography } from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";

import { useCrimeLabel } from "@/api/loadAlerts";
import { isSmallScreen, mailTo } from "@/common/utils";
import { captureMiddleFrameFromBlob } from "@/components/alerts/add/DisplayAlert/utils";
import { SingleAlertContext } from "@/components/alerts/context";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useServiceConfigurations } from "@/hooks/useCommunities";
import { IUserPermission, MediaBlob } from "@/types";

import { generateAlertPDF } from "./generateAlertPDF";
import { prepareImageForPDF } from "./pdfImage";

enum PDFButtonText {
  PreparingPDF = "preparing_pdf",
  GeneratePDF = "generate_pdf",
  HidePDFPreview = "hide_pdf_preview",
  DownloadPDF = "download_pdf",
}

const { PreparingPDF, GeneratePDF, HidePDFPreview, DownloadPDF } =
  PDFButtonText;

const generatePDFName = (
  bulletinId: string,
  issuedTime: string,
  occurrenceNumbers: string[],
  policeShortName?: string,
) => {
  const parts: string[] = [];

  if (policeShortName?.length) {
    parts.push(policeShortName);
  }

  // Cap at three occurrence numbers to keep the filename length reasonable
  const occurrences = occurrenceNumbers.filter(Boolean).slice(0, 3);
  if (occurrences.length) {
    parts.push(occurrences.join("_"));
  }

  parts.push(`BulletinID ${bulletinId}`);

  const issuedDate = dayjs(issuedTime);
  if (issuedDate.isValid()) {
    parts.push(`Issued On ${issuedDate.format("MMMM D YYYY")}`);
  }

  return `${parts.join("-")}.pdf`;
};

interface DownloadReportPDFProps {
  policeLogo: any;
  /**
   * Fetches the bulletin's media. Called on click rather than passed in
   * already-loaded: the readers who never press this button -- most of them,
   * on the emailed view-token link -- should not pay to download it.
   */
  loadMediaBlobs: () => Promise<MediaBlob[]>;
  issuedTime: string | undefined;
}

const DownloadReportPDF = ({
  policeLogo,
  loadMediaBlobs,
  issuedTime,
}: DownloadReportPDFProps) => {
  const alertProperties = useContext(SingleAlertContext).alertProperties;
  const getCrimeLabel = useCrimeLabel();
  const serviceConfigurations = useServiceConfigurations();
  const [isDisplayingPDF, setIsDisplayingPDF] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");

  const { currentUser } = useContext(AuthStateContext);
  const [isReadOnlyDialogOpen, setIsReadOnlyDialogOpen] = useState(false);
  const [alertPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const readOnly = alertPermissions
    ? !alertPermissions.canCreateBulletin
    : false;
  const { t } = useTranslation("bulletin");

  // Fetch published-resolution image attachments as blobs so they can be
  // embedded in the PDF (mirrors how evidence images are loaded)
  const fetchResolutionImageBlobs = async () => {
    const imageAttachments = (alertProperties.resolutions ?? [])
      .filter((resolution) => resolution.published)
      .flatMap((resolution) => Object.values(resolution.attachments ?? {}))
      .filter((attachment) => attachment.content_type?.includes("image"));

    const results = await Promise.all(
      imageAttachments.map(async (attachment) => {
        try {
          // Fetch the blob directly: the PDF embeds it via its own
          // URL.createObjectURL in generatePDFFromBlobs (revoked there), so we
          // must not create an orphan object URL here.
          const response = await fetch(attachment.download_url, {
            credentials: "include",
            cache: "no-cache",
          });
          if (!response.ok) {
            throw new Error("Failed to fetch resolution attachment");
          }
          const blob = await prepareImageForPDF(await response.blob());
          return { filename: attachment.filename, blob };
        } catch (err) {
          console.error("Error fetching resolution attachment", err);
          return null;
        }
      }),
    );

    return results.filter(
      (result): result is { filename: string; blob: Blob } => result !== null,
    );
  };

  const generatePDFFromBlobs = async (
    blobs: MediaBlob[],
    resolutionBlobs: { filename: string; blob: Blob }[] = [],
  ) => {
    const evidenceBlobUrls = new Map(
      blobs.map((b) => [b.filename, URL.createObjectURL(b.blob)]),
    );
    const resolutionBlobUrls = new Map(
      resolutionBlobs.map((b) => [b.filename, URL.createObjectURL(b.blob)]),
    );

    const downloadable = await pdf(
      generateAlertPDF({
        alertProperties,
        policeLogo,
        evidenceArray: blobs,
        evidenceBlobUrls,
        resolutionBlobUrls,
        issuedDate: issuedTime,
        currentUser,
        getCrimeLabel,
      }),
    ).toBlob();

    evidenceBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    resolutionBlobUrls.forEach((url) => URL.revokeObjectURL(url));

    const pdfUrl = URL.createObjectURL(downloadable);
    setPdfUrl(pdfUrl);
    return downloadable;
  };

  // Process media (extract a frame from videos, downscale photos), fetch
  // resolution images, and build the PDF. Runs only on user action so
  // view-token/anonymous viewers do not pay this potentially heavy work on
  // initial render.
  const processAndGeneratePDF = async () => {
    const mediaBlobs = await loadMediaBlobs();
    const processedBlobs = await Promise.all(
      mediaBlobs.map(async (blob) => {
        const media =
          blob.media_type === "video"
            ? await new Promise<MediaBlob>((resolve) => {
                captureMiddleFrameFromBlob(blob.blob, (extractedImageBlob) => {
                  if (extractedImageBlob) {
                    resolve({
                      blob: extractedImageBlob,
                      media_type: "video_thumbnail",
                      filename: blob.filename,
                    });
                  } else {
                    resolve(blob);
                  }
                });
              })
            : blob;
        // A video whose frame could not be captured is still a video; there is
        // no image to prepare.
        if (media.media_type === "video") return media;
        return { ...media, blob: await prepareImageForPDF(media.blob) };
      }),
    );
    const resolutionBlobs = await fetchResolutionImageBlobs();
    return generatePDFFromBlobs(processedBlobs, resolutionBlobs);
  };

  const pdfName = () => {
    const { bulletinId, issuedTime, policeService, occurrenceNumbers } =
      alertProperties;
    let shortName = "";
    if (policeService?.name) {
      shortName = serviceConfigurations[policeService.name]?.shortName || "";
    }

    return generatePDFName(
      bulletinId?.toString() || "",
      issuedTime?.toISOString() || "",
      occurrenceNumbers || [],
      shortName,
    );
  };

  const isMobileView = isSmallScreen();

  const buttonText = () => {
    if (isGenerating) {
      return PreparingPDF;
    } else if (isDisplayingPDF) {
      return HidePDFPreview;
    }
    return GeneratePDF;
  };

  const handleGeneratePDFDesktop = async () => {
    if (!readOnly) {
      if (!isDisplayingPDF) {
        setIsGenerating(true);
        try {
          await processAndGeneratePDF();
        } finally {
          setIsGenerating(false);
        }
      }
      setIsDisplayingPDF(!isDisplayingPDF);
    } else {
      setIsReadOnlyDialogOpen(true);
    }
  };

  const handleGeneratePDFMobile = async () => {
    if (!readOnly) {
      setIsGenerating(true);
      try {
        const downloadable = await processAndGeneratePDF();
        saveAs(downloadable, pdfName());
      } finally {
        setIsGenerating(false);
      }
    } else {
      setIsReadOnlyDialogOpen(true);
    }
  };

  return (
    <>
      {!isMobileView && (
        <>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleGeneratePDFDesktop}
            style={{
              width: "100%",
              marginBottom: buttonText() === HidePDFPreview ? "16px" : "0",
            }}
            disabled={isGenerating}
          >
            {t(buttonText())}
          </Button>

          {pdfUrl && isDisplayingPDF && (
            <>
              <iframe
                src={`${pdfUrl}#toolbar=0`}
                width="100%"
                height="1000px"
                style={{
                  marginBottom: "16px",
                }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  saveAs(pdfUrl, pdfName());
                }}
                style={{ width: "100%" }}
              >
                {t(DownloadPDF)}
              </Button>
            </>
          )}
          <DisabledComponentDialog
            isDialogOpen={isReadOnlyDialogOpen}
            handleClose={() => {
              setIsReadOnlyDialogOpen(false);
            }}
            title="You do not have access to downloading this bulletin."
            message={
              <Typography>
                Please contact{" "}
                <Link
                  rel="noopener noreferrer"
                  target="_blank"
                  href={mailTo({
                    email: "support@tryrubicon.com",
                    subject: "Requesting Access to Download Bulletins",
                    body: `Hello Rubicon Support Team, 
  
                    I would like to request access to download bulletins. I am trying to download a bulletin with id ${alertProperties.bulletinId}.
  
                    Thank you. 
  
                    Sincerely,
  
                    ${currentUser?.first_name} ${currentUser?.last_name} 
                    ${currentUser?.email}`,
                  })}
                  color="secondary"
                >
                  support@tryrubicon.com
                </Link>{" "}
                to request access.
              </Typography>
            }
          />
        </>
      )}
      {isMobileView && (
        <>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleGeneratePDFMobile}
            style={{ width: "100%" }}
            disabled={isGenerating}
          >
            {t(isGenerating ? PreparingPDF : GeneratePDF)}
          </Button>
          <DisabledComponentDialog
            isDialogOpen={isReadOnlyDialogOpen}
            handleClose={() => {
              setIsReadOnlyDialogOpen(false);
            }}
            title="You do not have access to downloading this bulletin."
            message={
              <Typography>
                Please contact{" "}
                <Link
                  rel="noopener noreferrer"
                  target="_blank"
                  href={mailTo({
                    email: "support@tryrubicon.com",
                    subject: "Requesting Access to Download Bulletins",
                    body: `Hello Rubicon Support Team, 
  
                    I would like to request access to download bulletins. I am trying to download a bulletin with id ${alertProperties.bulletinId}.
  
                    Thank you. 
  
                    Sincerely,
  
                    ${currentUser?.first_name} ${currentUser?.last_name} 
                    ${currentUser?.email}`,
                  })}
                  color="secondary"
                >
                  support@tryrubicon.com
                </Link>{" "}
                to request access.
              </Typography>
            }
          />
        </>
      )}
    </>
  );
};

export default DownloadReportPDF;
